import React, { useMemo } from 'react';
import { useQueries } from 'react-query';
import styled from 'styled-components';

import { getInitiativeChildren } from '@/features/canvas/api/initiative-relationships';
import { TAB_HEIGHT } from '@/features/canvas/constants';
import { FilteredNodes, useEditorContext } from '@/features/canvas/contexts/editor-context';
import { components } from '@/types/api';

import { buildSectionTree, flattenTree } from '../../utils/treeUtils';

import { GanttSidebarSection } from './GanttSidebarSection';

interface GanttSidebarProps {
  enhancedFilteredNodes: FilteredNodes[];
  expandedRows: Set<number>;
  onSectionHeightChange: (height: number, sectionId: number) => void;
  initiativesWithFields?: components['schemas']['Initiative'][];
}

const GanttSidebar: React.FC<GanttSidebarProps> = ({
  onSectionHeightChange,
  enhancedFilteredNodes,
  expandedRows,
  initiativesWithFields,
}) => {
  const { sortedVerticalSections, sortedHorizontalSections, roadmapNodes } = useEditorContext();

  // Combine sections exactly like other Gantt components
  const allSections = useMemo(
    () => [...sortedVerticalSections, ...sortedHorizontalSections],
    [sortedVerticalSections, sortedHorizontalSections]
  );

  // Combine enhanced filtered nodes with children from roadmapNodes (same as other columns)
  const allEnhancedNodes = useMemo(() => {
    // Start with the enhanced filtered nodes (which have proper dates)
    const result = [...enhancedFilteredNodes];

    // Find IDs that are already in enhancedFilteredNodes
    const enhancedNodeIds = new Set(enhancedFilteredNodes.map(n => n.id));

    // Find children in roadmapNodes that aren't in enhancedFilteredNodes
    const missingChildren = roadmapNodes.filter(node => !enhancedNodeIds.has(node.id));

    // Add the missing children with enhancement including initiative fields data
    missingChildren.forEach(childNode => {
      // Find the corresponding initiative with fields if available
      const initiativeWithFields = initiativesWithFields?.find(
        initiative => initiative.id === childNode.initiatives?.id
      );

      result.push({
        ...childNode,
        // Use initiative with fields if available, otherwise use the original
        initiatives: initiativeWithFields || childNode.initiatives,
        color: '',
        shape: 'circle' as const,
        size: undefined,
        isVisible: true,
      });
    });

    return result;
  }, [enhancedFilteredNodes, roadmapNodes, initiativesWithFields]);

  // Get unique initiative IDs for fetching children relationships
  const initiativeIds = useMemo(() => {
    const ids = new Set<number>();
    allEnhancedNodes.forEach(node => {
      if (node.initiatives?.id) {
        ids.add(node.initiatives.id);
      }
    });
    return Array.from(ids);
  }, [allEnhancedNodes]);

  // Fetch children for all initiatives
  const childrenQueries = useQueries(
    initiativeIds.map(id => ({
      queryKey: ['getInitiativeChildren', id],
      queryFn: () => getInitiativeChildren({ initiativeId: id }),
      enabled: true,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }))
  );

  // Check if queries are still loading
  const areQueriesLoading = childrenQueries.some(query => query.isLoading);

  // Create parent-child relationships map from query data
  const parentChildMap = useMemo(() => {
    // Don't build relationships until queries are done loading
    if (areQueriesLoading) {
      return new Map<number, components['schemas']['Initiative'][]>();
    }

    const map = new Map<number, components['schemas']['Initiative'][]>();
    childrenQueries.forEach((query, index) => {
      if (query.data && query.data.length > 0) {
        const parentId = initiativeIds[index]; // Use index to correlate with initiativeIds
        map.set(parentId, query.data);
      }
    });
    return map;
  }, [childrenQueries, initiativeIds, areQueriesLoading]);

  // Group initiatives by section using tree structure (same as other columns)
  const sectionTreeData = useMemo(() => {
    const result: Record<number, { depth: number; node: FilteredNodes }[]> = {};

    allSections.forEach(section => {
      if (section.id !== undefined) {
        // Use ALL nodes for this section (including children that were filtered out)
        const allSectionNodes = allEnhancedNodes.filter(
          node => node.sectionId === section.id && node.initiatives
        );

        // Build tree structure using ALL nodes
        const tree = buildSectionTree(allSectionNodes, parentChildMap);

        // Flatten the tree based on expanded state
        const flattened = flattenTree(tree, expandedRows);

        // Convert flattened tree items to format with depth info
        const sectionItems = flattened.map(item => ({
          node: item.node,
          depth: item.depth,
        }));

        result[section.id] = sectionItems;
      }
    });

    return result;
  }, [allEnhancedNodes, allSections, parentChildMap, expandedRows]);

  return (
    <SidebarContainer>
      {allSections.map((section, index) => {
        const treeItems = sectionTreeData[section.id!] || [];
        return (
          <GanttSidebarSection
            key={section.id}
            sectionId={section.id!}
            title={section.title}
            isOdd={index % 2 === 0}
            treeItems={treeItems}
            onHeightChange={onSectionHeightChange}
          />
        );
      })}
    </SidebarContainer>
  );
};

const SidebarContainer = styled.div`
  width: ${TAB_HEIGHT}px;
  min-height: 100%;
  background: ${({ theme }) => theme.palette.brand.backgroundMain};
  padding-bottom: 17px; /* Add space for scrollbar compensation */
`;

export { GanttSidebar };
